import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import TopAppBar from "../../components/TopAppBar";
import MainLayout from "../../layouts/MainLayout";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  VerticalErrorResponse,
  listVerticals,
  createVertical,
  renameVertical,
  deleteVertical,
  VerticalListItem,
} from "../../services/verticalManagementService";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";
import { RolePermissions } from "../../models/role";
import { handleError } from "../../utils/utilFunctions";
import VerticalDeleteDialogs from "../../components/VerticalManagement/VerticalDeleteDialogs";

type DialogState =
  | { mode: "closed" }
  | { mode: "new" }
  | { mode: "rename"; vertical: VerticalListItem }
  | { mode: "delete"; vertical: VerticalListItem };

const dialogBackdropProps = {
  sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
};
const dialogPaperProps = {
  sx: {
    width: "600px",
    maxWidth: "calc(100% - 64px)",
    maxHeight: "calc(100% - 64px)",
    borderRadius: "20px",
  },
};
const dialogTitleSx = {
  padding: "40px 40px 0",
  fontSize: "24px",
  fontWeight: 500,
  lineHeight: "29px",
};
const dialogContentSx = {
  padding: "8px 40px 30px",
  "&:first-of-type": { paddingTop: "8px" },
};
const dialogBodySx = {
  color: "#666",
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "28px",
  marginBottom: "16px",
};
const dialogActionsSx = { padding: "0 40px 40px", gap: "12px" };

const isVerticalListResponse = (data: unknown): data is { verticals: VerticalListItem[] } =>
  typeof data === "object" && data !== null && "verticals" in data;

const isVerticalErrorResponse = (data: unknown): data is VerticalErrorResponse =>
  typeof data === "object" && data !== null && "error" in data;

const verticalManagementSuccessToastOptions = {
  position: "bottom-left" as const,
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: false,
  pauseOnFocusLoss: false,
  draggable: true,
  progress: undefined,
  theme: "colored" as const,
  transition: Bounce,
};

const dialogButtonSx = {
  flex: 1,
  height: "56px",
  borderRadius: "8px",
  fontSize: "20px",
  fontWeight: 500,
};
const dialogCancelButtonSx = {
  ...dialogButtonSx,
  backgroundColor: "#C1E7FE",
  color: "#000",
  "&:hover": { backgroundColor: "#A9DCFC" },
};
const dialogPrimaryButtonSx = {
  ...dialogButtonSx,
  backgroundColor: "#0265DC",
  color: "#FFF",
  "&:hover": { backgroundColor: "#0257BE" },
  "&.Mui-disabled": {
    backgroundColor: "#0265DC",
    color: "#FFF",
    opacity: 0.6,
  },
};

const VerticalManagementScreen: React.FC = () => {
  const navigate = useNavigate();
  const rolePermissions = useRolePermissionsStore(
    (state: { rolePermissions: RolePermissions | null }) =>
      state.rolePermissions,
  );

  const [verticals, setVerticals] = useState<VerticalListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [dialogName, setDialogName] = useState("");
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{
    element: HTMLElement;
    vertical: VerticalListItem;
  } | null>(null);
  const lastMenuTriggerRef = useRef<HTMLElement | null>(null);

  const handleUnauthorized = () => {
    setDialog({ mode: "closed" });
    setMenuAnchor(null);
    handleError({ status: 401, toast, errorMessage: "", onClose: () => {} });
  };

  useEffect(() => {
    if (rolePermissions && !rolePermissions.verticalManagement) {
      toast.error("You do not have permission to manage verticals.");
      navigate("/main");
    }
  }, [rolePermissions, navigate]);

  const loadVerticals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listVerticals();
      if (response.status === 401) {
        setVerticals([]);
        handleUnauthorized();
        return;
      }
      if (response.status === 403) {
        toast.error("You no longer have access to manage verticals.");
        navigate("/main");
        return;
      }
      if (response.status === 200 && isVerticalListResponse(response.data)) {
        setVerticals(response.data.verticals);
      } else {
        setError("Failed to load verticals. Please try again.");
      }
    } catch {
      setError("Failed to load verticals. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (rolePermissions?.verticalManagement) {
      loadVerticals();
    }
  }, [rolePermissions, loadVerticals]);

  const handleOpenNew = () => {
    setDialog({ mode: "new" });
    setDialogName("");
    setDialogError(null);
  };

  const handleOpenRename = (vertical: VerticalListItem) => {
    lastMenuTriggerRef.current = menuAnchor?.element ?? null;
    setDialog({ mode: "rename", vertical });
    setDialogName(vertical.name);
    setDialogError(null);
    setMenuAnchor(null);
  };

  const handleOpenDelete = (vertical: VerticalListItem) => {
    lastMenuTriggerRef.current = menuAnchor?.element ?? null;
    setDialog({ mode: "delete", vertical });
    setDialogError(null);
    setMenuAnchor(null);
  };

  const handleDialogClose = () => {
    if (!saving) {
      const menuTrigger = lastMenuTriggerRef.current;
      lastMenuTriggerRef.current = null;
      setDialog({ mode: "closed" });
      setDialogName("");
      setDialogError(null);
      window.setTimeout(() => menuTrigger?.focus());
    }
  };

  const handleCreate = async () => {
    if (saving) return;
    if (!dialogName.trim()) {
      setDialogError("Vertical name is required.");
      return;
    }

    setSaving(true);
    setDialogError(null);
    try {
      const response = await createVertical(dialogName.trim());
      if (response.status === 201 || response.status === 200) {
        toast.success(
          "Vertical created successfully.",
          verticalManagementSuccessToastOptions,
        );
        setDialog({ mode: "closed" });
        setDialogName("");
        loadVerticals();
      } else if (response.status === 401) {
        handleUnauthorized();
      } else if (response.status === 409) {
        setDialogError(`A vertical named "${dialogName}" already exists.`);
      } else if (response.status === 400 && isVerticalErrorResponse(response.data)) {
        setDialogError(response.data.message || response.data.error || "Invalid vertical name.");
      } else if (response.status === 403) {
        toast.error("You no longer have access to manage verticals.");
        setDialog({ mode: "closed" });
        navigate("/main");
      } else {
        setDialogError("Failed to create vertical. Please try again.");
      }
    } catch {
      setDialogError("Failed to create vertical. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async () => {
    if (!dialogName.trim()) {
      setDialogError("Vertical name is required.");
      return;
    }
    if (dialog.mode !== "rename") return;
    if (saving) return;

    const verticalId = dialog.vertical.id;

    setSaving(true);
    setDialogError(null);
    try {
      const response = await renameVertical(verticalId, dialogName.trim());
      if (response.status === 200) {
        toast.success(
          "Vertical renamed successfully.",
          verticalManagementSuccessToastOptions,
        );
        setDialog({ mode: "closed" });
        setDialogName("");
        loadVerticals();
      } else if (response.status === 401) {
        handleUnauthorized();
      } else if (response.status === 409) {
        setDialogError(`A vertical named "${dialogName}" already exists.`);
      } else if (response.status === 400 && isVerticalErrorResponse(response.data)) {
        setDialogError(response.data.message || response.data.error || "Invalid vertical name.");
      } else if (response.status === 403) {
        toast.error("You no longer have access to manage verticals.");
        setDialog({ mode: "closed" });
        navigate("/main");
      } else {
        setDialogError("Failed to rename vertical. Please try again.");
      }
    } catch {
      setDialogError("Failed to rename vertical. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (dialog.mode !== "delete") return;
    if (saving) return;
    const verticalId = dialog.vertical.id;
    const verticalName = dialog.vertical.name;

    setSaving(true);
    setDialogError(null);
    try {
      const response = await deleteVertical(verticalId);
      if (response.status === 204 || response.status === 200) {
        toast.success(
          `Vertical "${verticalName}" deleted successfully.`,
          verticalManagementSuccessToastOptions,
        );
        setDialog({ mode: "closed" });
        loadVerticals();
      } else if (response.status === 401) {
        handleUnauthorized();
      } else if (response.status === 404) {
        toast.info("This vertical no longer exists.");
        setDialog({ mode: "closed" });
        loadVerticals();
      } else if (response.status === 403) {
        toast.error("You no longer have access to manage verticals.");
        setDialog({ mode: "closed" });
        navigate("/main");
      } else {
        setDialogError("Failed to delete vertical. Please try again.");
      }
    } catch {
      setDialogError("Failed to delete vertical. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    vertical: VerticalListItem,
  ) => {
    setMenuAnchor({ element: event.currentTarget, vertical });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  if (!rolePermissions) {
    return null;
  }

  return (
    <>
      <TopAppBar
        pageTitle="Manage Verticals"
        isShowDetails
        btnClose="Close"
        onClose={() => navigate("/main/verticals")}
        isLoading={loading}
      />
      <MainLayout>
        <Box
          sx={{
            padding: "0 0.25rem",
            fontFamily: "Inter",
            "& .MuiTableCell-root": {
              boxSizing: "border-box",
              height: "60px",
              fontFamily: "Inter",
            },
            "& .MuiTypography-root, & .MuiButton-root": { fontFamily: "Inter" },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ paddingTop: "9px", marginBottom: "40px" }}
          >
            <Box />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenNew}
              disabled={loading}
              sx={{ width: "157px", height: "40px", whiteSpace: "nowrap" }}
            >
              New Vertical
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ marginBottom: "1rem" }}>
              {error}
              <Button
                size="small"
                onClick={loadVerticals}
                sx={{ marginLeft: "1rem" }}
              >
                Retry
              </Button>
            </Alert>
          )}

          {loading ? (
            <TableContainer
              sx={{
                border: "1px solid #C7C7C7",
                borderRadius: "0.5rem",
                maxHeight: "763px",
              }}
            >
              <Table stickyHeader aria-label="Verticals" aria-busy="true">
                <TableHead sx={{ backgroundColor: "#F0F4F8" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vertical</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Tagged Content
                    </TableCell>
                    <TableCell aria-label="Actions" sx={{ width: "50px" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        <Skeleton variant="text" width={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={200} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={100} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="circular" width={40} height={40} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              {verticals.length === 0 ? (
                <Box
                  sx={{
                    padding: "3rem",
                    textAlign: "center",
                    border: "1px solid #C7C7C7",
                    borderRadius: "0.5rem",
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No verticals have been created.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    border: "1px solid #C7C7C7",
                    borderRadius: "0.5rem",
                    maxHeight: "763px",
                    width: "100%",
                  }}
                >
                  <Table stickyHeader aria-label="Verticals">
                    <TableHead sx={{ backgroundColor: "#F0F4F8" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Vertical</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Tagged Content
                        </TableCell>
                        <TableCell aria-label="Actions" sx={{ width: "50px" }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {verticals.map((vertical, index) => (
                        <TableRow
                          key={vertical.id}
                          hover
                          selected={menuAnchor?.vertical.id === vertical.id}
                        >
                          <TableCell component="th" scope="row">{index + 1}</TableCell>
                          <TableCell>{vertical.name}</TableCell>
                          <TableCell>
                            {vertical.assignedContentCount === 1
                              ? "1 video"
                              : `${vertical.assignedContentCount} videos`}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, vertical)}
                              aria-label={`Actions for ${vertical.name}`}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>

        <Menu
          anchorEl={menuAnchor?.element}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => menuAnchor && handleOpenRename(menuAnchor.vertical)}
          >
            <EditIcon fontSize="small" sx={{ marginRight: "0.5rem" }} />
            Rename
          </MenuItem>
          <MenuItem
            onClick={() => menuAnchor && handleOpenDelete(menuAnchor.vertical)}
          >
            <DeleteIcon
              fontSize="small"
              sx={{ marginRight: "0.5rem", color: "error.main" }}
            />
            Delete
          </MenuItem>
        </Menu>

        <Dialog
          open={dialog.mode === "new"}
          onClose={handleDialogClose}
          maxWidth={false}
          BackdropProps={dialogBackdropProps}
          PaperProps={{
            sx: {
              ...dialogPaperProps.sx,
              minHeight: "317px",
            },
            ...(saving ? { "aria-busy": true } : {}),
          }}
        >
          <DialogTitle sx={dialogTitleSx}>
            New Vertical
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <Typography sx={dialogBodySx}>
              Add a business vertical. It’s available for tagging right away.
            </Typography>
            {saving && <Box role="status" aria-live="polite" sx={{ mb: 1 }}>Creating vertical…</Box>}
            <TextField
              autoFocus
              label="Vertical Name"
              type="text"
              fullWidth
              variant="outlined"
              value={dialogName}
              onChange={(e) => setDialogName(e.target.value)}
              error={Boolean(dialogError)}
              helperText={dialogError}
              disabled={saving}
              InputProps={{ sx: { borderRadius: "8px" } }}
            />
          </DialogContent>
          <DialogActions disableSpacing sx={dialogActionsSx}>
            <Button
              onClick={handleDialogClose}
              disabled={saving}
              sx={dialogCancelButtonSx}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={saving || !dialogName.trim()}
              startIcon={saving && <CircularProgress size={20} />}
              sx={dialogPrimaryButtonSx}
            >
              Create Vertical
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dialog.mode === "rename"}
          onClose={handleDialogClose}
          maxWidth={false}
          BackdropProps={dialogBackdropProps}
          PaperProps={{
            sx: dialogPaperProps.sx,
            ...(saving ? { "aria-busy": true } : {}),
          }}
        >
          <DialogTitle sx={dialogTitleSx}>
            Rename "{dialog.mode === "rename" && dialog.vertical.name}"
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <Typography sx={dialogBodySx}>
              The new name updates everywhere it’s used — tags and browse nav.
            </Typography>
            {saving && <Box role="status" aria-live="polite" sx={{ mb: 1 }}>Renaming vertical…</Box>}
            <TextField
              autoFocus
              label="Vertical Name"
              type="text"
              fullWidth
              variant="outlined"
              value={dialogName}
              onChange={(e) => setDialogName(e.target.value)}
              error={Boolean(dialogError)}
              helperText={dialogError}
              disabled={saving}
              InputProps={{ sx: { borderRadius: "8px" } }}
            />
          </DialogContent>
          <DialogActions disableSpacing sx={dialogActionsSx}>
            <Button
              onClick={handleDialogClose}
              disabled={saving}
              sx={dialogCancelButtonSx}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRename}
              variant="contained"
              disabled={saving || !dialogName.trim() || (dialog.mode === "rename" && dialogName === dialog.vertical.name)}
              startIcon={saving && <CircularProgress size={20} />}
              sx={dialogPrimaryButtonSx}
            >
              Rename Vertical
            </Button>
          </DialogActions>
        </Dialog>
        {dialog.mode === "delete" && (
          <VerticalDeleteDialogs
            dialog={dialog}
            dialogError={dialogError}
            saving={saving}
            onClose={handleDialogClose}
            onDelete={handleDelete}
          />
        )}
      </MainLayout>
    </>
  );
};

export default VerticalManagementScreen;
